import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Container, Spinner } from "reactstrap";

import {
  acceptPublicQuote,
  getPublicQuote,
} from "../../../services/publicQuoteService";
import { CrmQuote } from "../../../services/crmService";

type ApiClientError = { data?: string };

const PublicQuotePage: React.FC = () => {
  const { t } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<CrmQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);
  const [nextSteps, setNextSteps] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPublicQuote(token);
        if (!cancelled) {
          setQuote(data);
        }
      } catch (err) {
        if (!cancelled) {
          const apiError = err as ApiClientError;
          setError(apiError?.data || t("crm.public.loadError"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const handleAccept = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await acceptPublicQuote(token);
      setQuote(result.quote);
      setDoneMessage(result.studio_message || t("crm.public.accepted"));
      setNextSteps(result.next_steps || null);
    } catch (err) {
      const apiError = err as ApiClientError;
      setError(apiError?.data || t("crm.public.acceptError"));
    } finally {
      setSaving(false);
    }
  };

  const total = quote
    ? (quote.total_amount / 100).toLocaleString(undefined, {
        style: "currency",
        currency: "BRL",
      })
    : "";

  const studioName = quote?.studio?.name;

  document.title = studioName
    ? t("crm.public.pageTitleStudio", { studio: studioName })
    : t("crm.public.pageTitle");

  return (
    <div className="auth-page-wrapper py-5 d-flex justify-content-center align-items-center min-vh-100 bg-light">
      <Container style={{ maxWidth: 480 }}>
        <div className="bg-white border rounded-3 p-4 shadow-sm">
          <p className="text-uppercase text-muted small mb-1">
            {studioName || "Adatis"}
          </p>
          <h1 className="h4 mb-3">
            {studioName
              ? t("crm.public.titleStudio", { studio: studioName })
              : t("crm.public.title")}
          </h1>

          {loading && (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          )}

          {!loading && error && <p className="text-danger">{error}</p>}

          {!loading && quote && (
            <>
              <p className="mb-1 text-muted">{t("crm.public.totalLabel")}</p>
              <p className="fs-3 mb-3">{total}</p>
              <ul className="list-unstyled mb-4">
                {(quote.items ?? []).map((item, index) => (
                  <li key={item.id ?? index} className="mb-2">
                    {item.service_package?.name
                      || item.service?.name
                      || t("crm.public.itemFallback")}
                    {" — "}
                    {((item.unit_price * item.quantity) / 100).toLocaleString(undefined, {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </li>
                ))}
              </ul>

              {doneMessage || quote.status === "accepted" ? (
                <>
                  <p className="text-success mb-2">
                    {doneMessage || t("crm.public.accepted")}
                  </p>
                  {nextSteps && <p className="text-muted mb-0">{nextSteps}</p>}
                </>
              ) : (
                <Button
                  color="dark"
                  className="w-100"
                  disabled={saving || quote.status === "expired"}
                  onClick={() => void handleAccept()}
                >
                  {saving ? <Spinner size="sm" /> : t("crm.public.accept")}
                </Button>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default PublicQuotePage;
